import { useCallback, useEffect, useMemo, useState } from 'react';
import { CommonProperties } from '../../common/common.interfaces';
import { useLoading } from '../../loading/LoadingContext';
import { useUserContext } from '../../user/UserContext';
import { DateTime } from 'luxon';
import { SalsifyConfigurationCombinedTableRow } from '../salsify.interfaces';
import { apiFetch } from '../../utility/fetch';
import { API_PROD_URL } from '../..';
import { Badge } from 'react-bootstrap';
import JSONPretty from 'react-json-pretty';
import { CUSTOM_JSON_THEME } from '../../common/common.constants';
import { Download } from 'react-bootstrap-icons';
import { notify } from '../../notify/notify';

interface SalsifyConfigurationProps extends CommonProperties {}

export default function SalsifyConfiguration(props: SalsifyConfigurationProps) {
  const {toggleLoading, changeStep, changeSubStep} = useLoading()
  const {userData} = useUserContext()
  const [rows, setRows]: [SalsifyConfigurationCombinedTableRow[], any] = useState([])

  const [refresh, setRefresh] = useState(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))

  useEffect(() => {
    const load = async function(){
      changeStep('Salsify Sync')
      changeSubStep('Loading configuration records')
      toggleLoading(true)

      const response = await apiFetch<SalsifyConfigurationCombinedTableRow[]>({
        endpoint: 'azureTables',
        method: 'GET',
        accessToken: userData.accessToken,
        baseUrlOverride: API_PROD_URL,
        params: [
          {key: 'table', value: 'salsifyConfiguration'}
        ]
      })
      console.log(response.body)
      setRows(response.body)

      toggleLoading(false)
    }
    load()
  }, [refresh])

  const handleDownloadClicked = useCallback((e) => {
    const fromSalsify = rows.find(r => r.reportStatus === 'complete')
    if(!fromSalsify){
      notify('error', 'Report is pending or errored! Cannot download at this time.')
      return
    }

    const payload = JSON.parse(fromSalsify.response)
    
    // Trigger file download
    const link = document.createElement('a');
    link.href = payload.url;
    link.setAttribute('download', ''); // Optional: specify filename here
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [rows])

  const view = useMemo(() => {
    if(!rows || rows.length === 0) return
    const fromSalsify = rows?.find(r => r.partitionKey === 'from-salsify')
    const toSalsify = rows.find(r => r.partitionKey === 'to-salsify')
    return <div style={{display: 'flex', flexWrap: 'wrap', gap: '5px'}}>
      <div className='beacon-card' style={{width: '40%'}}>
        <span className='beacon-header'>FROM SALSIFY</span>
        <br/>
        <span className='beacon-label'>UPDATED ON {DateTime.fromISO(fromSalsify.timestamp).setZone('America/Chicago').toFormat('MM/dd/yyyy t')}. RUNS EVERY {fromSalsify.cadenceInMinutes} MINUTES(S).</span>
        <br/>
        <div>
        <span className='beacon-label'>REPORT STATUS</span>&nbsp;
        <Badge bg={fromSalsify.reportStatus === 'pending' ? 'warning' : fromSalsify.reportStatus === 'complete' ? 'success' : 'danger'}>{fromSalsify.reportStatus.toUpperCase()}</Badge>
        <span style={{float: 'right'}}>
          <span className='beacon-link' title='Downloads the report once it is complete.' onClick={handleDownloadClicked}>DOWNLOAD LAST REPORT <Download size={25} /></span> 
        </span>
        </div>
        <div>
          <span className='beacon-label'>LAST RESPONSE</span>
          <JSONPretty data={fromSalsify.response} theme={CUSTOM_JSON_THEME} style={{ fontSize: '1.5em' }} />
        </div>
      </div>
      <div className='beacon-card' style={{width: '40%'}}>
        <span className='beacon-header'>TO SALSIFY</span>
        <br/>
        <span className='beacon-label'>UPDATED ON {DateTime.fromISO(toSalsify.timestamp).setZone('America/Chicago').toFormat('MM/dd/yyyy t')}. RUNS EVERY {toSalsify.cadenceInMinutes} MINUTE(S).</span>
      </div>
    </div>
  }, [rows])

  return view
}